import { Box, FormControl, MenuItem, Select, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetTeams } from "../../../../api/teams";
import { useSnackbar } from "../../../../shared/components/snackbar/Snackbar";
import { useActiveTeamContext } from "../../../../hooks/useActiveTeamContext";
import {
  setLastKnownActiveTeamName,
  switchActiveTeamAndNavigateToProfileList,
} from "../../../../utils/active-team/active-team";
import { ALL_TEAMS_LIST_QUERY } from "../teams/shared/team.constants";
import {
  getDefaultTeamId,
  getSelectableTeams,
} from "../teams/shared/team.utils";
import PreferredTeamSelector from "./PreferredTeamSelector";

const CurrentTeamSelectedDropdown: React.FC = () => {
  const { t: translate } = useTranslation(["profile", "common"]);
  const { showSnackbar } = useSnackbar();
  const { currentTeamId } = useActiveTeamContext();

  const { data: teamsData, isLoading: isTeamsLoading } = useGetTeams(
    ALL_TEAMS_LIST_QUERY
  );

  const teams = useMemo(
    () => getSelectableTeams(teamsData?.teams ?? []),
    [teamsData?.teams]
  );

  const selectedTeamId = useMemo(() => {
    if (isTeamsLoading || teams.length === 0) {
      return "";
    }

    if (currentTeamId && teams.some((team) => team.id === currentTeamId)) {
      return currentTeamId;
    }

    return getDefaultTeamId(teams);
  }, [currentTeamId, isTeamsLoading, teams]);

  useEffect(() => {
    if (teams.length === 0) {
      return;
    }

    const cacheTeamName = (teamId: string | null | undefined): void => {
      if (!teamId) {
        return;
      }

      const team = teams.find((item) => item.id === teamId);
      if (team?.name) {
        setLastKnownActiveTeamName(team.name, teamId);
      }
    };

    cacheTeamName(selectedTeamId);
    cacheTeamName(currentTeamId);
  }, [currentTeamId, selectedTeamId, teams]);

  const handleTeamChange = useCallback(
    (event: SelectChangeEvent<string>): void => {
      const nextTeamId = event.target.value;

      if (!nextTeamId || nextTeamId === selectedTeamId) {
        return;
      }

      try {
        const selectedTeam = teams.find((team) => team.id === nextTeamId);
        switchActiveTeamAndNavigateToProfileList(
          nextTeamId,
          selectedTeam?.name
        );
      } catch {
        showSnackbar(
          translate("common:apiResponse.unexpectedErrorOccurred"),
          true
        );
      }
    },
    [selectedTeamId, showSnackbar, teams, translate]
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        flexWrap: "wrap",
        pb: 2,
        gap: 2,
      }}
    >
      <Typography sx={{ minWidth: 160, fontWeight: 500, pt: 0.5 }}>
        {translate("profiles.currentTeamSelected.label")}:
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          minWidth: 320,
        }}
      >
        <FormControl size="small" disabled={isTeamsLoading} fullWidth>
          <Select
            value={selectedTeamId}
            onChange={handleTeamChange}
            aria-label="current-team-selected-dropdown"
            displayEmpty
          >
            {teams.map((team) => (
              <MenuItem key={team.id} value={team.id}>
                {team.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <PreferredTeamSelector currentTeamId={selectedTeamId} />
      </Box>
    </Box>
  );
};

export default CurrentTeamSelectedDropdown;
